import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import { Form } from '@/components/ui/form';
import SpinnerButton from '@/components/common/SpinnerButton';
import LoadingPage from '@/components/common/LoadingPage';

import FormGeneral from './components/common/FormGeneral';
import { useLocations, usePutLocation, useLocation } from '@/data/location';
import DeleteLocationDialog from './components/editPage/DeleteLocationDialog';

const formSchema = z.object({
  name: z.string().min(1),
  regionId: z.string().min(1),
  address: z.string().optional(),
  lat: z.number(),
  lng: z.number(),
  activities: z
    .array(z.string())
    .min(1, { message: 'Must select at least 1 activity' })
});

const EditPage = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const updateLocation = usePutLocation();
  const locations = useLocations();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation(params.id);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: location.data.name,
      regionId: location.data.regionId,
      address: location.data.address || '',
      lat: parseFloat(location.data.lat),
      lng: parseFloat(location.data.lng),
      activities: location.data.activities.map((a) => a.activityId)
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const updatedLocation = await updateLocation(params.id, values);
      location.mutate(updatedLocation);
      locations.mutate((currentLocations) =>
        currentLocations.map((loc) =>
          loc.id === params.id ? updatedLocation : loc
        )
      );
      setSubmitting(false);
      navigate('/location');
    } catch (err) {
      setApiError(JSON.stringify(err.message));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/location">Locations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Edit</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between">
            Edit {location.data.name}
            <DeleteLocationDialog location={location.data} />
          </CardTitle>
          <CardDescription>Edit the details of this location.</CardDescription>
        </CardHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, (err) => console.log(err))}
          >
            <div className="mx-auto max-w-120">
              <CardContent className="">
                <div className="space-y-4">
                  <FormGeneral />
                  {apiError && <Error error={apiError} />}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <SpinnerButton loading={submitting}>Save</SpinnerButton>
              </CardFooter>
            </div>
          </form>
        </Form>
      </Card>
    </>
  );
};

const EditPageLoader = () => {
  const params = useParams();
  const location = useLocation(params.id);

  if (location.isLoading && !location.data) {
    return <LoadingPage />;
  }

  return <EditPage />;
};

export default EditPageLoader;
